import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import { createIdPathFromNode } from "../navigation/createIdPathFromNode";
import {
	updateNodeFromSubscriptionResult,
	updateNodeFromNotificationData,
	setPropertiesToNotReadable,
} from "../state 2 interface/updateNodeFromSubscriptionReply";
import { updateEntityPermission } from "../state 2 interface/permissions";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import SubscriptionBuilder from "../state 2 interface/SubscriptionBuilder";
import {
	SubscribableObject,
	CollectionKeyRegistration,
	asRegisterable,
	getCollectionRegistrations,
	needsToBeSubscribed,
	EntriesRegistrationNode,
} from "../state/registerClientState";
import * as microtaskQueue from "../microtaskQueue";
import DestructorTree from "../DestructorTree";
import { setNodeContextToLocal } from "../state/setLocation";
import { createVariable, awaitCondition } from "../../../../../stream";
import { iCollection } from "../state/Collection";

export const pendingSubscriptions = createVariable(0, true);

function subscriptionsInitialized() {
	return awaitCondition(pendingSubscriptions, (queue) => queue === 0);
}

class Subscription {
	private subscribed_properties = 0;
	private destructor = new DestructorTree();
	private queued = false;
	readonly builder = SubscriptionBuilder.create();

	constructor(private _node: api_cs.Cnode) {
		this._node.onDestroy(() => this.destroy());
	}
	get node(): api_cs.Cnode {
		return this._node;
	}
	get empty(): boolean {
		return this.subscribed_properties <= 0;
	}
	add_dependency(object: SubscribableObject) {
		const registration = asRegisterable(object);
		registration.__needs_subscription = false;
		this.subscribed_properties += 1;
		registration.__subscriptions?.onDestroy(() => {
			this.subscribed_properties -= 1;
			if (this.empty) {
				this.destroy();
			}
		});
	}

	private datastore_subscription: { unsubscribe: () => void } | undefined;
	subscribe() {
		if (this.datastore_subscription) {
			return;
		}
		this.queue();

		this.datastore_subscription = DatastoreProxy.instance.subscribe(
			{
				"context node": createIdPathFromNode(this.node),
				"mutation permissions": ["include", {}],
				"node subscription": { attributes: this.builder.properties },
			},
			(subscription_result) => {
				microtaskQueue.add(() => {
					if (this.node.destroyed) {
						return;
					}
					subscription_result.properties.context.switch({
						"does not exist": () => {
							setNodeContextToLocal(this.node);
						},
						"permission denied": () => {
							setPropertiesToNotReadable(this.node);
						},
						exists: ($) => {
							updateNodeFromSubscriptionResult(this.node, $, this.destructor);
							updateEntityPermission(this.node);
						},
					});
					this.dequeue();
				});
			},
			(notification) => {
				microtaskQueue.add(() => {
					if (this.node.destroyed) {
						return;
					}
					notification.properties.context.switch({
						"does not exist": () => {
							const position = this.node.input.position().variant;
							switch (position.name) {
								case "root":
									break;
								case "state":
									break;
								case "group":
									break;
								case "entity":
									switch (position.definition.variant.name) {
										case "root":
											break;
										case "entry": {
											const entry = position.definition.variant.definition;
											iCollection(entry.parent).deleteServerEntry(entry);
											break;
										}
									}
									break;
							}
						},
						"permission denied": () => {
							setPropertiesToNotReadable(this.node);
						},
						exists: ($) => {
							updateNodeFromNotificationData(this.node, $.properties.notification_data, this.destructor);
						},
					});
				});
			},
			() => {
				this.destroy();
			}
		);
	}
	queue(): void {
		if (!this.queued) {
			pendingSubscriptions.set(pendingSubscriptions.value + 1);
			this.queued = true;
		}
	}
	dequeue(): void {
		if (this.queued) {
			pendingSubscriptions.set(pendingSubscriptions.value - 1);
			this.queued = false;
		}
	}
	destroy(): void {
		this.datastore_subscription?.unsubscribe();
		this.datastore_subscription = undefined;
		this.dequeue();
		this.destructor.destroy();
	}
}

function walkAttributes(node: api_cs.Cnode, subscription: Subscription, subscription_builder: SubscriptionBuilder) {
	for (const [attribute_name, attribute] of node.properties.attributes) {
		switch (attribute.properties.type.state.name) {
			case "operation": {
				const operation = attribute.properties.type.state.node;
				switch (operation.properties.type.state.name) {
					case "command": {
						const command = operation.properties.type.state.node;
						if (needsToBeSubscribed(command)) {
							subscription.add_dependency(command);
							subscription_builder.command(attribute_name);
						}
						break;
					}
					case "action":
						break;
					default:
						unexpectedState(operation.properties.type.state);
				}
				break;
			}
			case "property": {
				const property = attribute.properties.type.state.node;
				switch (property.properties.type.state.name) {
					case "text": {
						const text = property.properties.type.state.node;
						if (needsToBeSubscribed(text)) {
							subscription.add_dependency(text);
							const text_def = text.inferences.text_type();
							switch (text_def.properties.has_reference.state.name) {
								case "yes":
									subscription_builder.constrained_text(attribute_name);
									break;
								case "no":
									subscription_builder.text(attribute_name);
									break;
								default:
									unexpectedState(text_def.properties.has_reference.state);
							}
						}
						break;
					}
					case "file": {
						const file = property.properties.type.state.node;
						if (needsToBeSubscribed(file)) {
							subscription.add_dependency(file);
							subscription_builder.file(attribute_name);
						}
						break;
					}
					case "number": {
						const number = property.properties.type.state.node;
						if (needsToBeSubscribed(number)) {
							subscription.add_dependency(number);
							subscription_builder.number(attribute_name);
						}
						break;
					}
					case "group": {
						const group = property.properties.type.state.node;
						if (needsToBeSubscribed(group)) {
							subscription.add_dependency(group);
						}
						walkAttributes(group.properties.node, subscription, subscription_builder.group(attribute_name));
						break;
					}
					case "state group": {
						const stategroup = property.properties.type.state.node;
						const def = stategroup.inferences.state_group_type();
						if (needsToBeSubscribed(stategroup)) {
							subscription.add_dependency(stategroup);
							subscription_builder.state_group(attribute_name, def);
						}
						for (const [state_name, state] of stategroup.properties.states) {
							switch (state.properties.location.state.name) {
								case "remote":
									walkAttributes(
										state.properties.node,
										subscription,
										subscription_builder.state(attribute_name, state_name, def)
									);
									break;
								case "local":
									break;
								default:
									unexpectedState(state.properties.location.state);
							}
						}
						break;
					}
					case "collection": {
						const collection = property.properties.type.state.node;
						const collection_registrations = getCollectionRegistrations(collection);

						// Find out if entries need to be subscribed
						const unsubscribed_key_registrations = new Set<CollectionKeyRegistration>();
						const unsubscribed_keys: string[] = [];
						for (const [key, key_registration] of collection_registrations.keys) {
							if (needsToBeSubscribed(key_registration)) {
								unsubscribed_key_registrations.add(key_registration);
								unsubscribed_keys.push(key);
							}
						}
						const include_graph_endpoints = includeGraphEndpoints(collection);

						const entries_subscription_builder = subscription_builder.collection_entries(
							attribute_name,
							[],
							include_graph_endpoints
						);
						const needs_entries_subscription =
							walkEntriesRegistrationNode(
								collection_registrations.entries.node,
								subscription,
								entries_subscription_builder
							) || needsToBeSubscribed(collection_registrations.entries);
						if (needs_entries_subscription) {
							subscription.add_dependency(collection_registrations.entries);
						} else {
							subscription_builder.remove(attribute_name);
							if (unsubscribed_key_registrations.size > 0) {
								subscription_builder.collection_entries(attribute_name, unsubscribed_keys, include_graph_endpoints);
								for (const entry_registration of unsubscribed_key_registrations) {
									subscription.add_dependency(entry_registration);
								}
							} else if (needsToBeSubscribed(collection_registrations.updates)) {
								subscription.add_dependency(collection_registrations.updates);
								subscription_builder.collection_updates(attribute_name, include_graph_endpoints);
							}
						}
						break;
					}
					default:
						unexpectedState(property.properties.type.state);
				}
				break;
			}
			default:
				unexpectedState(attribute.properties.type.state);
		}
	}
}

export const GraphEndpointsSubscribed = new WeakMap<api_cs.Ccollection, true>();
function includeGraphEndpoints(collection: api_cs.Ccollection) {
	switch (collection.properties.has_ordered_graphs.state.name) {
		case "yes": {
			const is_subscribed = GraphEndpointsSubscribed.get(collection) ?? false;
			if (!is_subscribed) {
				GraphEndpointsSubscribed.set(collection, true);
			}
			return is_subscribed ? "no" : "yes";
		}
		case "no":
			return "no";
	}
}

function walkEntriesRegistrationNode(
	node: EntriesRegistrationNode,
	subscription: Subscription,
	subscription_builder: SubscriptionBuilder
): boolean {
	let needs_subscription = false;
	for (const [name, att] of node) {
		switch (att[0]) {
			case "command": {
				const $ = att[1];
				if (needsToBeSubscribed($)) {
					subscription.add_dependency($);
					subscription_builder.command(name);
					needs_subscription = true;
				}
				break;
			}
			case "text": {
				const $ = att[1];
				if (needsToBeSubscribed($)) {
					subscription.add_dependency($);
					if (att[1].reference) {
						subscription_builder.constrained_text(name);
					} else {
						subscription_builder.text(name);
					}
					needs_subscription = true;
				}
				break;
			}
			case "number": {
				const $ = att[1];
				if (needsToBeSubscribed($)) {
					subscription.add_dependency($);
					subscription_builder.number(name);
					needs_subscription = true;
				}
				break;
			}
			case "file": {
				const $ = att[1];
				if (needsToBeSubscribed($)) {
					subscription.add_dependency($);
					subscription_builder.file(name);
					needs_subscription = true;
				}
				break;
			}
			case "group": {
				const $ = att[1];
				if (needsToBeSubscribed($)) {
					subscription.add_dependency($);
					needs_subscription = true;
				}
				const group_needs_subscription = walkEntriesRegistrationNode(
					$.node,
					subscription,
					subscription_builder.group(name)
				);
				needs_subscription = needs_subscription || group_needs_subscription;
				break;
			}
			case "stategroup": {
				const $ = att[1];
				if (needsToBeSubscribed($)) {
					subscription.add_dependency($);
					subscription_builder.state_group(name, $.def);
					needs_subscription = true;
				}
				for (const [state_name, state_tree] of $.states) {
					const state_needs_subscription = walkEntriesRegistrationNode(
						state_tree,
						subscription,
						subscription_builder.state(name, state_name, $.def)
					);
					needs_subscription = needs_subscription || state_needs_subscription;
				}
				break;
			}
			case "collection": {
				const $ = att[1];
				if (needsToBeSubscribed($.updates)) {
					subscription.add_dependency($.updates);
					subscription_builder.collection_updates(name);
					needs_subscription = true;
				}
				break;
			}
			default:
				unexpectedState(att[0]);
		}
	}
	return needs_subscription;
}

const nodeUpdateQueue = new Set<api_cs.Cnode>();

function createNodeSubscriptions() {
	for (const node of nodeUpdateQueue) {
		if (!node.destroyed) {
			const subscription = new Subscription(node);
			walkAttributes(node, subscription, subscription.builder);
			if (!subscription.empty) {
				subscription.subscribe();
			} else {
				subscription.destroy();
			}
		}
	}
	nodeUpdateQueue.clear();
}

export function queue(object: SubscribableObject) {
	const node = object.component_root.input.entity().output.node();
	switch (node.input.position().output.root().variant.name) {
		case "operation":
			// Do not subscribe entries of operations
			break;
		case "root":
			nodeUpdateQueue.add(node);
			if (nodeUpdateQueue.size === 1) {
				microtaskQueue.add(() => createNodeSubscriptions());
			}
			break;
	}
}

export async function finishedAndInitialized(): Promise<void> {
	await microtaskQueue.done();
	await subscriptionsInitialized();
}

export function size(): number {
	return nodeUpdateQueue.size + pendingSubscriptions.value;
}
